import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, KeyRound, Loader2, ShieldOff, Bot } from "lucide-react";

import { McpOAuthGrants } from "./McpOAuthGrants";
import { ScopeHelp } from "@/components/landing/ScopeHelp";

const B = process.env.REACT_APP_BACKEND_URL;
const ADM = `${B}/api/mcp-admin/google-ads`;
const copy = (t) => navigator.clipboard.writeText(t).then(() => toast.success("Copiat"));

const Snip = ({ id, text }) => (
  <div className="flex items-start gap-2 rounded-lg bg-zinc-950 px-3 py-2 font-mono text-[11px] text-zinc-300">
    <code className="flex-1 break-all" data-testid={id}>{text}</code>
    <button data-testid={`${id}-copy`} onClick={() => copy(text)} className="text-zinc-500 hover:text-amber-300"><Copy className="h-3.5 w-3.5" /></button>
  </div>
);

const Calls = ({ calls }) => (
  <div className="max-h-56 overflow-y-auto rounded-lg border border-zinc-800" data-testid="mcp-calls">
    {!calls.length ? <p className="p-3 text-xs text-zinc-500" data-testid="mcp-calls-empty">Niciun apel încă.</p> : calls.map((c, i) => (
      <div key={i} className="flex gap-3 border-b border-zinc-800/60 px-3 py-1.5 text-[11px] last:border-0" data-testid={`mcp-call-${i}`}>
        <span className="w-32 shrink-0 text-zinc-500">{new Date(c.at).toLocaleString("ro-RO")}</span>
        <span className={`w-48 shrink-0 font-medium ${c.ok ? "text-emerald-300" : "text-rose-300"}`}>{c.tool}</span>
        <span className="flex-1 truncate text-zinc-400" title={c.error || JSON.stringify(c.args)}>{c.error || JSON.stringify(c.args)}</span>
      </div>))}
  </div>
);

const LimitEditor = ({ value, max, onSaved }) => {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  const save = async () => {
    try { await axios.put(`${ADM}/limit`, { max_daily_ron: Number(v) }); toast.success(`Limita AI: ${v} lei/zi`); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  return (
    <div className="flex items-center gap-2 text-xs text-zinc-400" data-testid="mcp-limit">
      Buget zilnic maxim pe care îl poate seta AI-ul:
      <input data-testid="mcp-limit-input" type="number" min={5} max={max} value={v} onChange={(e) => setV(e.target.value)} className="w-20 rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1 text-zinc-100" />
      lei/zi (max {max})
      {Number(v) !== Number(value) && <button data-testid="mcp-limit-save" onClick={save} className="rounded-full bg-amber-400 px-3 py-0.5 font-semibold text-zinc-950">Salvează</button>}
    </div>
  );
};

export const McpAccess = () => {
  const [d, setD] = useState(null);
  const [tok, setTok] = useState("");
  const [busy, setBusy] = useState(false);
  const [reauth, setReauth] = useState(false);
  const load = useCallback(() => axios.get(ADM).then((r) => setD(r.data)).catch(() => setD({ active: false, calls: [], tools: [] })), []);
  useEffect(() => { load(); }, [load]);
  const gen = async () => {
    if (d?.active && !window.confirm("Generez un token nou? Cel vechi nu va mai funcționa.")) return;
    setBusy(true);
    try { const r = await axios.post(`${ADM}/token`); setTok(r.data.token); load(); } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy(false);
  };
  const revoke = async () => {
    if (!window.confirm("Revoc accesul? Niciun AI nu se mai poate conecta până generezi alt token.")) return;
    await axios.delete(`${ADM}/token`); setTok(""); load(); toast.success("Acces revocat");
  };
  if (!d) return <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />;
  const url = `${window.location.origin}${d.path}`;
  const t = tok || "<TOKEN>";
  return (
    <div className="space-y-3" data-testid="mcp-access">
      <div className="flex items-center gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-zinc-200"><Bot className="h-4 w-4 text-amber-400" /> Acces AI (MCP) la Google Ads</h3>
        <span className={`rounded-full px-2 py-0.5 text-[11px] ${d.active ? "bg-emerald-500/15 text-emerald-300" : "bg-zinc-800 text-zinc-400"}`} data-testid="mcp-status">
          {d.active ? `activ · token …${d.token_hint}${d.last_used_at ? ` · folosit ${new Date(d.last_used_at).toLocaleString("ro-RO")}` : " · nefolosit încă"}` : "inactiv"}</span>
        <div className="ml-auto flex gap-2">
          <button data-testid="mcp-generate-btn" onClick={gen} disabled={busy} className="inline-flex items-center gap-1 rounded-full bg-amber-400 px-3 py-1 text-xs font-semibold text-zinc-950 disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <KeyRound className="h-3.5 w-3.5" />} {d.active ? "Token nou" : "Generează token"}</button>
          {d.active && <button data-testid="mcp-revoke-btn" onClick={revoke} className="inline-flex items-center gap-1 rounded-full border border-zinc-700 px-3 py-1 text-xs text-zinc-300 hover:border-rose-400"><ShieldOff className="h-3.5 w-3.5" /> Revocă</button>}
        </div>
      </div>
      <p className="text-xs text-zinc-400">Instrumente ({d.tools.length}): {d.tools.join(", ")}. Campaniile noi se creează pe pauză. Tot ce face AI-ul apare mai jos.</p>
      <LimitEditor value={d.max_daily_ron} max={d.hard_cap_ron} onSaved={load} />
      <button data-testid="mcp-shopify-reauth-btn" onClick={() => setReauth((v) => !v)} className="rounded-full border border-zinc-700 px-3 py-1 text-xs text-zinc-300 hover:border-amber-400">Reautorizează Shopify (permisiuni pentru tool-urile Shopify)</button>
      {reauth && <ScopeHelp title="Permisiuni Shopify pentru tool-urile MCP (pagini, politici, teme, fișiere, pixeli) — o singură dată:" onClose={() => setReauth(false)} />}
      {tok && <div className="space-y-1 rounded-lg border border-amber-400/50 bg-amber-400/5 p-3" data-testid="mcp-token-box">
        <p className="text-xs font-medium text-amber-200">Copiază tokenul acum — nu se mai afișează după ce închizi pagina.</p><Snip id="mcp-token" text={tok} /></div>}
      <McpOAuthGrants url={url} />
      <Snip id="mcp-url" text={url} />
      <p className="text-[11px] text-zinc-500">Claude Code:</p>
      <Snip id="mcp-snippet-claude-code" text={`claude mcp add --transport http bragus-google-ads ${url} --header "Authorization: Bearer ${t}"`} />
      <p className="text-[11px] text-zinc-500">Claude Desktop / alte aplicații (claude_desktop_config.json):</p>
      <Snip id="mcp-snippet-desktop" text={JSON.stringify({ mcpServers: { "bragus-google-ads": { command: "npx", args: ["-y", "mcp-remote", url, "--header", `Authorization: Bearer ${t}`] } } })} />
      <Calls calls={d.calls} />
    </div>
  );
};
